import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Search, Bell, ShieldCheck, ChevronDown, Settings as SettingsIcon, LogOut } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCompany } from "@/context/CompanyContext";
import { avatarFor } from "@/lib/utils-tg";
import CompanySwitcher from "@/components/CompanySwitcher";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

export default function Header() {
  const { user, logout } = useAuth();
  const { active } = useCompany();
  const loc = useLocation();
  const nav = useNavigate();
  // Role shown in the header = the user's role IN THE ACTIVE COMPANY, falling
  // back to their global role when no company context is available.
  const effectiveRole = active?.role || user?.role;
  // Always show a human-friendly name; fall back to the email handle so a
  // freshly-invited employee is never shown as blank next to their role.
  const displayName = user?.name?.trim() || (user?.email ? user.email.split("@")[0] : "User");

  return (
    <header
      className="sticky top-0 z-20 bg-white/80 backdrop-blur-md border-b border-slate-200 px-6 md:px-10 h-16 flex items-center gap-4"
      data-testid="app-header"
    >
      <CompanySwitcher />
      <div className="flex-1 max-w-md ml-2 md:ml-4 hidden md:block">
        <div className="flex items-center gap-2 bg-slate-100 rounded-xl px-3 h-9 text-sm text-slate-500">
          <Search className="h-4 w-4" />
          <input
            placeholder="Search anything…"
            className="bg-transparent border-0 focus:ring-0 focus:outline-none flex-1 text-sm placeholder:text-slate-400"
            data-testid="header-search"
          />
          <kbd className="text-[10px] font-semibold text-slate-400 bg-white border border-slate-200 rounded px-1.5 py-0.5">⌘K</kbd>
        </div>
      </div>
      <div className="flex items-center gap-2 ml-auto">
        {(effectiveRole === "admin") && (
          <button
            onClick={() => nav("/admin")}
            className="tg-btn-ghost text-xs"
            data-testid="header-admin-link"
            title="Admin"
          >
            <ShieldCheck className="h-4 w-4 text-emerald-600" /> Admin
          </button>
        )}
        <button
          className="h-9 w-9 rounded-xl hover:bg-slate-100 flex items-center justify-center text-slate-500 transition-colors relative"
          data-testid="header-notifications"
          onClick={() => nav("/inbox")}
        >
          <Bell className="h-4 w-4" />
        </button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-xl hover:bg-slate-100 transition-all"
              data-testid="header-profile-trigger"
            >
              <img
                src={user?.avatar_url || avatarFor(displayName)}
                onError={(e) => { e.currentTarget.src = avatarFor(displayName); }}
                alt=""
                className="h-9 w-9 rounded-full object-cover ring-2 ring-emerald-100"
                data-testid="header-avatar"
              />
              <div className="text-left leading-tight hidden sm:block">
                <div className="text-sm font-semibold text-slate-900 truncate max-w-[140px]" data-testid="header-user-name">{displayName}</div>
                <div className="text-[11px] text-slate-500 capitalize" data-testid="header-user-role">{effectiveRole}</div>
              </div>
              <ChevronDown className="h-4 w-4 text-slate-400 hidden sm:block" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" side="bottom" className="w-56">
            <DropdownMenuLabel className="font-normal">
              <div className="text-xs text-slate-500">Signed in as</div>
              <div className="text-sm font-semibold text-slate-800 truncate">{displayName}</div>
              <div className="text-[11px] text-slate-400 capitalize">{effectiveRole} · {active?.company_name || "No company"}</div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => nav("/settings")} data-testid="menu-settings">
              <SettingsIcon className="h-4 w-4 mr-2" /> Settings
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => { logout(); nav("/login"); }}
              data-testid="menu-logout"
            >
              <LogOut className="h-4 w-4 mr-2" /> Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
